import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { Modal } from '../modal'
import { Button } from '../button'
import { Input } from '../input'
import { downloadReceipt } from '../../utils/download'
import {
  GuestDeleteMode,
  GuestFiling,
  guestDeleteModeEnum,
} from '../../services/reservation-guests/models'

/** What the dialog offers, by what a delete would destroy:
 * - `simple`: nothing filed or receipted, single confirm → permanent
 * - `filed`: filed, not scrubbed → remove data (recommended) or delete permanently
 * - `permanentOnly`: scrubbed but still holds filing proof → delete permanently */
export type GuestDeleteTier = 'simple' | 'filed' | 'permanentOnly'

export const guestDeleteTier = ({
  scrubbed,
  hasProof,
}: {
  scrubbed: boolean
  hasProof: boolean
}): GuestDeleteTier => {
  if (!hasProof) return 'simple'
  return scrubbed ? 'permanentOnly' : 'filed'
}

interface Props {
  isOpen: boolean
  tier: GuestDeleteTier
  name: string
  filings: GuestFiling[]
  guestId: string
  receiptSystems: string[]
  isPending: boolean
  onDelete: (mode: GuestDeleteMode, confirmed: boolean) => void
  onCancel: () => void
}

export function GuestDeleteDialog({
  isOpen,
  tier,
  name,
  filings,
  guestId,
  receiptSystems,
  isPending,
  onDelete,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const confirmWord: string = t('reservationGuestCard.deleteConfirmWord')
  const typedMatches = typed.trim().toUpperCase() === confirmWord.toUpperCase()

  const handleCancel = () => {
    setTyped('')
    onCancel()
  }

  if (tier === 'simple') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={isPending ? () => {} : handleCancel}
        title={t('reservationGuestCard.deleteUnfiledTitle')}
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          {t('reservationGuestCard.deleteUnfiledMessage', { name })}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={handleCancel} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => onDelete(guestDeleteModeEnum.PERMANENT, false)}
            isLoading={isPending}
            data-testid="guest-delete-confirm"
          >
            {t('common.delete')}
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={isPending ? () => {} : handleCancel}
      title={
        tier === 'filed'
          ? t('reservationGuestCard.deleteFiledTitle')
          : t('reservationGuestCard.deleteScrubbedTitle')
      }
      size="md"
    >
      <div className="flex flex-col gap-3 text-sm text-text-secondary">
        {tier === 'filed' && <p>{t('reservationGuestCard.deleteFiledMessage', { name })}</p>}
        <div className="rounded border border-border p-3" data-testid="guest-delete-filing-warning">
          <p className="font-semibold text-error">
            {t('reservationGuestCard.deleteFilingWarning')}
          </p>
          {filings.length > 0 && (
            <ul className="mt-2 list-disc pl-5" data-testid="guest-delete-filings">
              {filings.map((filing) => (
                <li key={filing.system}>
                  {filing.system.toUpperCase()} ·{' '}
                  {filing.confirmationCode || t('reservationGuestCard.deleteFilingNoCode')}
                  {filing.submittedAt && ` · ${format(new Date(filing.submittedAt), 'yyyy-MM-dd')}`}
                </li>
              ))}
            </ul>
          )}
          {receiptSystems.length > 0 && (
            <div className="mt-2" data-testid="guest-delete-receipts">
              <p>{t('reservationGuestCard.deleteSaveReceiptsFirst')}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {receiptSystems.map((system) => (
                  <Button
                    key={system}
                    variant="text"
                    tone="primary"
                    size="sm"
                    icon={<ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => downloadReceipt(guestId, system)}
                  >
                    {t('reservationGuestCard.downloadReceipt', { system: system.toUpperCase() })}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
        <Input
          label={t('reservationGuestCard.deleteConfirmPrompt', { word: confirmWord })}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          data-testid="guest-delete-confirm-word"
        />
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="secondary" onClick={handleCancel} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="danger"
          onClick={() => onDelete(guestDeleteModeEnum.PERMANENT, true)}
          disabled={!typedMatches || isPending}
          data-testid="guest-delete-permanent"
        >
          {t('reservationGuestCard.deletePermanently')}
        </Button>
        {tier === 'filed' && (
          <Button
            variant="primary"
            onClick={() => onDelete(guestDeleteModeEnum.SCRUB, false)}
            isLoading={isPending}
            data-testid="guest-delete-scrub"
          >
            {t('reservationGuestCard.removePersonalData')}
          </Button>
        )}
      </div>
    </Modal>
  )
}
